//@flow
import React, {useState} from 'react';
import {makeStyles, Typography} from '@material-ui/core';
import type {MessageMultimedia} from '../../../../../../../../entities';
import {colors} from '../../../../../../../../assets/styles/colors';
import {fetchWhatsappApiMedia} from '../../../../../utils/message';
import {useCurrentConversation} from '../../../../../utils/conversation';
import InlineLoader from '../../../../../../../../elements/LoadingSpinner/InlineLoader';
import DownloadIcon from '../../../../../../../../elements/icons/DownloadIcon';
import {InsertDriveFileOutlined} from '@material-ui/icons';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    display: 'flex',
    gap: '.25rem',
    alignItems: 'center',
    minWidth: '250px',

    '&[data-self="true"]': {
      flexDirection: 'row-reverse',
    },
  },
  fileButton: {
    textDecoration: 'none',
    maxWidth: '100%',
    minWidth: 'min-content',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '.5rem',
    borderRadius: '.75rem',
    background: colors.grey200,
    color: colors.grey800,
    gap: '.5rem',
    border: 'none',
    cursor: 'pointer',
    transition: 'background .2s ease-in-out',

    '&:hover': {
      background: colors.grey300,
    },

    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.7,
    },
  },
  fileWrap: {
    width: '100%',
    display: 'flex',
    background: colors.grey300,
    gap: '.5rem',
    padding: '.5rem',
    alignItems: 'center',
    borderRadius: '.75rem',
  },
  fileName: {
    fontSize: '.8rem',
    fontWeight: 500,
    flexGrow: 1,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileType: {
    aspectRatio: '1/1',
    borderRadius: '.35rem',
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.grey600,
  },
  downloadIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    fontSize: '1.5rem',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusText: {
    fontSize: '.75rem',
    color: colors.grey600,
  },
}));

type Props = {
  document: MessageMultimedia,
  self?: boolean,
};

export default function DocumentMessage({document, self}: Props) {
  const classes = useStyles();
  const {currentConversation} = useCurrentConversation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    if (!currentConversation || !document.mediaId) return;

    setLoading(true);
    setError(false);

    try {
      const blobUrl = await fetchWhatsappApiMedia(
        currentConversation.deviceId,
        document.mediaId
      );

      // Create a temporary link to download the file
      const link = window.document.createElement('a');
      link.href = blobUrl;
      link.download = document.name || 'documento';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      // Revoke the blob URL after download
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const getStatusText = () => {
    if (loading) return 'Descargando...';
    if (error) return 'Error al descargar. Intenta de nuevo.';
    return 'Clic para descargar';
  };

  return (
    <div className={classes.container} data-self={self}>
      <button
        className={classes.fileButton}
        onClick={handleDownload}
        disabled={loading}
        type="button"
      >
        <div className={classes.fileWrap}>
          <span className={classes.fileType}>
            <InsertDriveFileOutlined />
          </span>

          <Typography className={classes.fileName}>
            {document.name || 'Documento'}
          </Typography>

          <span className={classes.downloadIcon}>
            {loading ? (
              <InlineLoader loading={loading} size={20} />
            ) : (
              <DownloadIcon />
            )}
          </span>
        </div>

        <Typography className={classes.statusText}>
          {getStatusText()}
        </Typography>
      </button>
    </div>
  );
}
